import React, {useCallback, useEffect, useState} from "react";
import ListItem from "@mui/material/ListItem";
import Box from "@mui/material/Box"; // Добавляем Box для управления layout
import CloseIcon from "@mui/icons-material/Close";
import {PROCESS_URL} from "shared/api/urls";
import ProgressBar from "components/ProgressBar";
import useApiPostRequestWithProgressHook from "shared/api/postRequestWithProgressHook";
import {checkFileExist} from "shared/api/checkFileExist";
import {useProcessTask} from "shared/hooks";

import RemoveItemDialog from "./RemoveItemDialog";
import {removeFile} from "../lib/fileHandlers";
import {getFileErrorMessage} from "../lib";
import {validateSvsFile} from "../lib/svsValidation";
import {FILE_EXISTS_ERROR_MESSAGE, UPLOADING_STATUS} from "../constants";

const FileListItem = ({
    url,
    file,
    index,
    files,
    status,
    tissue,
    typeObj,
    versionModel,
    setFiles,
    setStatus,
    uploadState,
    setUploadState,
    uploadQueue,
    setUploadQueue,
}) => {
    const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
    const [requestData, setRequestData] = useState(null);
    const [fileIsLoading, setFileIsLoading] = useState(false);
    
    // Используем кастомный хук для обработки задач
    const { processSlide, isProcessing, error: processError } = useProcessTask();

    const updateUploadProgress = (fileIndex, percentCompleted) => {
        setUploadState((prevState) => {
            const newState = [...prevState];
            newState[fileIndex] = {
                ...prevState[index],
                progress: percentCompleted,
            };
            return newState;
        });
    };

    const { isCompleted, isSuccessful, isFailed, response, error } =
        useApiPostRequestWithProgressHook(
            url,
            requestData,
            index,
            updateUploadProgress
        );

    useEffect(() => {
        setUploadState((prevState) => {
            return (prevState || []).map((elem) => {
                if (elem.id === index) {
                    return {
                        ...prevState[index],
                        status: isCompleted,
                    };
                }
                return elem;
            });
        });

        if (isCompleted) {
            setUploadQueue((prevState) => {
                const { loading, completed } = prevState;
                const currentFile = { id: index };

                const newLoading = loading.filter((elem) => elem.id !== index);

                const newCompleted = [...completed];
                newCompleted.push(currentFile);

                return {
                    ...prevState,
                    loading: newLoading,
                    completed: newCompleted,
                };
            });

            const postData = {
                direct_svs: response.name,
                name_tis: tissue,
                type_obj: typeObj,
                version_model: versionModel,
                id_slide: response.id,
                split_tis: 0,
            };

            // Отправляем на AI-разметку только если выбрана ИНС
            if (tissue) {
                const handleProcessSlide = async () => {
                    try {
                        await processSlide(response.id, postData, PROCESS_URL);
                    } catch (error) {
                        console.error(`Failed to process slide ${response.id}:`, error);
                    }
                };
                handleProcessSlide();
            }
        }
    }, [isCompleted,
        isSuccessful,
        isFailed,
        response,
        index,
        setUploadQueue,
        setUploadState,
        tissue,
        typeObj,
        versionModel,
        processSlide,]);

    useEffect(() => {
        if (error) {
            setUploadState((prevState) => {
                const newState = [...prevState];
                newState[index] = {
                    ...prevState[index],
                    error:
                        error.response?.data
                            ? error.response.data
                            : error.code === "ERR_NETWORK"
                                ? ["Ошибка подключения к серверу"]
                                : "Ошибка загрузки файла",
                };
                return newState;
            });
        }
    }, [error, index, setUploadState]);

    // Помечает файл как завершенный с ошибкой и пропускает его вперед по
    // очереди, чтобы остальные файлы продолжили загружаться
    const completeWithError = useCallback((message) => {
        setUploadState((prevState) => {
            const newState = [...prevState];
            newState[index] = {
                ...newState[index],
                error: { name: [message] },
                status: true, // Помечаем как завершенный
            };
            return newState;
        });

        setUploadQueue((prevState) => {
            const { loading, completed } = prevState;
            const currentFile = { id: index };

            const newLoading = loading.filter((elem) => elem.id !== index);
            const newCompleted = [...completed];
            newCompleted.push(currentFile);

            return {
                ...prevState,
                loading: newLoading,
                completed: newCompleted,
            };
        });
    }, [index, setUploadState, setUploadQueue]);

    const startLoading = useCallback(async () => {
        if (!file || !file.file || !file.file.name) {
            return;
        }

        // Загружаем только .svs: проверяем расширение и содержимое файла,
        // чтобы не отправлять на сервер файл с подмененным расширением
        const validationError = await validateSvsFile(file.file);
        if (validationError) {
            completeWithError(validationError);
            return;
        }

        const exists = await checkFileExist(file.file.name);
        if (exists) {
            completeWithError(FILE_EXISTS_ERROR_MESSAGE);
            return;
        }

        let data = new FormData();
        data.append("svs", file.file, file.file.name);
        data.append("tissue", tissue || "");
        data.append("name", file.file.name);

        setRequestData(data);
    }, [file, tissue, setRequestData, completeWithError]);


    useEffect(() => {
        if (status === UPLOADING_STATUS.Loading) {
            const { waiting, loading } = uploadQueue;
            const currentWaitingFile = waiting.filter((file) => file.id === index);

            // Разрешаем старт загрузки только если:
            // 1) файл уже не в очереди ожидания (значит он в loading)
            // 2) файл является ПЕРВЫМ в очереди loading — обеспечиваем последовательную загрузку
            const isFirstInLoading =
                Array.isArray(loading) && loading.length > 0 && loading[0]?.id === index;

            if (currentWaitingFile.length === 0 && isFirstInLoading && !fileIsLoading) {
                setFileIsLoading(true);
                startLoading();
            }
        }
    }, [uploadQueue, status, fileIsLoading, index, startLoading]);

    // Текст ошибки файла: проверка формата, дубликат имени или ответ сервера
    const errorMessage = getFileErrorMessage(uploadState[index]?.error);

    return (
        <>
            <ListItem key={index} divider>
                {/* Используем Box для управления layout */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between", // Выравниваем элементы по краям
                        width: "100%",
                    }}
                >
                    {/* <span>{status}</span> */}
                    {/* Левая часть: порядковый номер и имя файла */}
                    <span>{file.serialNumber}. {file.file.name}</span>

                    {/* Правая часть: ProgressBar или другие элементы */}
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        {status === UPLOADING_STATUS.Loading && !errorMessage && !isCompleted && !uploadState[index]?.status && (
                            <ProgressBar
                                sx={{ marginLeft: 2 }} // Добавляем отступ слева
                                percentage={uploadState[index]?.progress}
                            />
                        )}
                        {/* Итоговый статус файла: ошибка проверки/загрузки
                            или успешное завершение */}
                        {errorMessage && (
                            <span style={{ color: 'red', marginLeft: 8 }}>
                                {errorMessage}
                            </span>
                        )}
                        {!errorMessage && processError && (
                            <span style={{ color: 'orange', marginLeft: 8 }}>
                                Ошибка обработки
                            </span>
                        )}
                        {!errorMessage && isCompleted && isSuccessful && !isFailed && (
                            <span>Загрузка успешно завершена!</span>
                        )}
                        {isProcessing && (
                            <span style={{ color: 'blue', marginLeft: 8 }}>
                                Отправка на анализ...
                            </span>
                        )}
                        <CloseIcon
                            onClick={() => setRemoveDialogOpen(true)}
                            sx={{ marginLeft: 2, cursor: "pointer" }}
                        />
                    </Box>
                </Box>
            </ListItem>
            <RemoveItemDialog
                index={index}
                fileName={file.file.name}
                removeFile={() =>
                    removeFile(index, files, setFiles, setStatus, setUploadState, setUploadQueue)
                }
                removeDialogOpen={removeDialogOpen}
                setRemoveDialogOpen={setRemoveDialogOpen}
            />
        </>
    );
};

export { FileListItem };
